import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../utils/api";
import { setToken } from "../utils/authToken";
import { toast } from "sonner";
import { Brain, TrendingUp, Shield, BarChart3, Globe, Bell, Zap, Target, Mail, KeyRound, Loader2, Eye, EyeOff, UserPlus } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { AuthModeToggle } from "../components/auth/AuthModeToggle";

const MIN_PASSWORD = 8;

const describeAuthError = (error, mode) => {
  if (!error.response) return error.request ? "Network error - please check your connection" : (error.message || "Request failed");
  const detail = error.response.data?.detail;
  if (detail && typeof detail === "object") return { code: detail.code, message: detail.message };
  if (Array.isArray(detail)) return detail.map(d => d.msg).join(", ");
  return detail || error.response.data?.message || `Server error: ${error.response.status}` || (mode === "register" ? "Registration failed" : "Login failed");
};

export default function Login() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [mode, setMode] = useState("login"); // "login" | "register"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Cookie (web) or secure-store Bearer (native) — just ask the server.
        const response = await api.get(`/auth/me`);
        if (response.data) navigate('/dashboard', { replace: true });
      } catch { setChecking(false); }
    };
    checkAuth();
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) { toast.error("Enter email and password"); return; }
    if (mode === "register" && password.length < MIN_PASSWORD) { toast.error(`Password must be at least ${MIN_PASSWORD} characters`); return; }

    setSubmitting(true);
    try {
      const endpoint = mode === "register" ? `/auth/register` : `/auth/login`;
      const body = mode === "register" ? { email: email.trim(), password, name: name.trim() || undefined } : { email: email.trim(), password };
      const response = await api.post(endpoint, body);
      const user = response.data;
      // Native only: persist Bearer token in the OS secure store.
      // Web: nothing stored — the HttpOnly cookie is the session.
      await setToken(user.session_token);
      toast.success(mode === "register" ? `Account created! Welcome, ${user.name || user.email}` : `Welcome back, ${user.name || user.email}!`);
      navigate('/dashboard', { replace: true, state: { user } });
    } catch (error) {
      const described = describeAuthError(error, mode);
      if (described?.code === "no_account") {
        setMode("register");
        toast.error("No account exists for this email yet — create one below.", { duration: 6000 });
      } else {
        toast.error(typeof described === "string" ? described : described.message);
      }
    } finally { setSubmitting(false); }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  const features = [
    { icon: <Shield className="w-6 h-6 text-blue-400" />, title: "IP Asset Protection", desc: "Track trademarks with expiration alerts" },
    { icon: <BarChart3 className="w-6 h-6 text-purple-400" />, title: "Smart Portfolio", desc: "Real-time P&L tracking and performance analytics" },
    { icon: <Brain className="w-6 h-6 text-emerald-400" />, title: "AI Stock Intelligence", desc: "Gemini-powered recommendations and pattern recognition" },
    { icon: <Bell className="w-6 h-6 text-yellow-400" />, title: "Price Alerts", desc: "Get notified when stocks hit your target prices" },
    { icon: <TrendingUp className="w-6 h-6 text-pink-400" />, title: "IPO Research", desc: "Analyze upcoming IPOs before they launch" },
    { icon: <Target className="w-6 h-6 text-cyan-400" />, title: "Asset Sniper", desc: "Automated asset valuation and expiry triage" },
    { icon: <Globe className="w-6 h-6 text-indigo-400" />, title: "Market Intelligence", desc: "Fear & Greed Index, crypto prices, economic data" },
    { icon: <Zap className="w-6 h-6 text-orange-400" />, title: "Automated Monitoring", desc: "24/7 background jobs tracking your assets" },
  ];

  return (
    <main className="min-h-screen bg-[#09090b] noise-overlay relative overflow-hidden">
      <div className="hero-glow absolute inset-0 pointer-events-none" aria-hidden="true" />
      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-12">
        <div className="max-w-7xl w-full">
          {/* Logo and Title */}
          <header className="text-center mb-12">
            <img 
              src="/zero-deficit-logo.png" 
              alt="Zero Deficit™: Pulse Logo" 
              className="w-32 h-32 mx-auto mb-6 drop-shadow-2xl"
              width="128"
              height="128"
              loading="eager"
            />
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white mb-3 tracking-tight">Zero Deficit&#8482;: Pulse</h1>
            <p className="text-xl sm:text-2xl text-zinc-400 mb-2">Financial Intelligence Platform</p>
            <p className="text-zinc-500">Track Assets &bull; Analyze Stocks &bull; Make Smarter Decisions</p>
            <button
              onClick={() => navigate('/about')}
              className="mt-4 text-sm text-purple-400 hover:text-purple-300 transition-colors"
              data-testid="login-about-link"
            >
              Learn more about Pulse →
            </button>
          </header>

          <div className="grid lg:grid-cols-3 gap-8 items-start">
            {/* Features */}
            <section className="lg:col-span-2" aria-labelledby="features-heading">
              <h2 id="features-heading" className="text-2xl font-bold text-white mb-6 text-center lg:text-left">Powerful Features</h2>
              <ul className="grid sm:grid-cols-2 gap-4" role="list">
                {features.map((f) => (
                  <li key={f.title} className="p-4 bg-zinc-900/50 rounded-lg border border-zinc-800 hover:border-zinc-700 transition-all">
                    <article className="flex items-start gap-3">
                      <div className="p-2 bg-zinc-800/50 rounded-lg flex-shrink-0" aria-hidden="true">{f.icon}</div>
                      <div><h3 className="text-white font-semibold text-sm mb-1">{f.title}</h3><p className="text-zinc-400 text-xs">{f.desc}</p></div>
                    </article>
                  </li>
                ))}
              </ul>
            </section>

            {/* Auth Card */}
            <aside className="order-first lg:order-none lg:sticky lg:top-8" aria-label="Authentication">
              <Card className="glass border-zinc-800 shadow-2xl">
                <CardHeader className="text-center pb-4 space-y-4">
                  <AuthModeToggle mode={mode} onChange={setMode} />
                  <div>
                    <CardTitle className="text-2xl text-white">{mode === "register" ? "Create your account" : "Welcome back"}</CardTitle>
                    <CardDescription className="text-zinc-400">
                      {mode === "register" ? "Free — you'll be signed in right away" : "New here? Tap Create account above"}
                    </CardDescription>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4 px-8 pb-8">
                  <form onSubmit={handleSubmit} className="space-y-3" data-testid="auth-form">
                    {mode === "register" && (
                      <div className="relative">
                        <UserPlus className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                        <Input type="text" placeholder="Your name (optional)" value={name} onChange={e => setName(e.target.value)}
                          className="bg-zinc-900/80 border-zinc-700 text-white placeholder:text-zinc-500 pl-10 py-5" data-testid="register-name-input" />
                      </div>
                    )}
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <Input type="email" inputMode="email" autoCapitalize="none" autoCorrect="off" autoComplete="email" placeholder="Email address" value={email} onChange={e => setEmail(e.target.value)}
                        className="bg-zinc-900/80 border-zinc-700 text-white placeholder:text-zinc-500 pl-10 py-5" data-testid="login-email-input" />
                    </div>
                    <div className="relative">
                      <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <Input type={showPassword ? "text" : "password"} placeholder={mode === "register" ? `Password (${MIN_PASSWORD}+ characters)` : "Password"} value={password} onChange={e => setPassword(e.target.value)}
                        minLength={mode === "register" ? MIN_PASSWORD : undefined} autoComplete={mode === "register" ? "new-password" : "current-password"}
                        className="bg-zinc-900/80 border-zinc-700 text-white placeholder:text-zinc-500 pl-10 pr-10 py-5" data-testid="login-password-input" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300">
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {mode === "register" && (
                      <p className={`text-xs ${password && password.length < MIN_PASSWORD ? "text-amber-400" : "text-zinc-500"}`} data-testid="password-hint">
                        {password && password.length < MIN_PASSWORD ? `${MIN_PASSWORD - password.length} more character${MIN_PASSWORD - password.length === 1 ? "" : "s"} needed` : `Use at least ${MIN_PASSWORD} characters`}
                      </p>
                    )}
                    <Button type="submit" disabled={submitting}
                      className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-5 transition-all hover:scale-[1.02]"
                      data-testid="login-submit-btn">
                      {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : mode === "register" ? "Create Account" : "Sign In"}
                    </Button>
                  </form>

                  <div className="text-center pt-2">
                    {mode === "login" ? (
                      <p className="text-sm text-zinc-500">
                        Don't have an account?{" "}
                        <button onClick={() => setMode("register")} className="text-purple-400 hover:text-purple-300 font-semibold" data-testid="switch-to-register">Sign up</button>
                      </p>
                    ) : (
                      <p className="text-sm text-zinc-500">
                        Already have an account?{" "}
                        <button onClick={() => setMode("login")} className="text-purple-400 hover:text-purple-300 font-semibold" data-testid="switch-to-login">Sign in</button>
                      </p>
                    )}
                  </div>

                  <div className="mt-3 p-3 bg-zinc-900/50 rounded-lg border border-zinc-800">
                    <p className="text-xs text-zinc-400 text-center">
                      By signing in, you agree to our{" "}
                      <button onClick={() => navigate('/disclaimer')} className="text-purple-400 hover:underline">Terms of Service</button>
                      {" "}and{" "}
                      <button onClick={() => navigate('/privacy-policy')} className="text-purple-400 hover:underline">Privacy Policy</button>.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </aside>
          </div>

          {/* Footer */}
          <footer className="mt-16 text-center">
            <div className="inline-flex items-center gap-6 text-sm text-zinc-500">
              <span>Trusted by investors</span><span aria-hidden="true">&bull;</span>
              <span>600+ stock lists</span><span aria-hidden="true">&bull;</span>
              <span>AI-powered insights</span><span aria-hidden="true">&bull;</span>
              <span>FREE to use</span>
            </div>
          </footer>
        </div>
      </div>
    </main>
  );
}
